import React, { useContext, useEffect, useState } from "react";
import { SignalRContext } from "./Admin";
import { Box, Button, FileInput, Grid, Image, Tab, Tabs, Text, Anchor, Select, TextInput, ResponsiveContext } from "grommet";
import axios from "axios";
import exifr from "exifr";
import { sum, orderBy, set } from "lodash";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import QRCodeView from "../components/QRCodeView";
import StyledModal from "../components/StyledModal";
import Log from "./Log";
import ScrollContainer from "../components/ScrollContainer";
import { Link, useNavigate } from "react-router-dom";
import LinkAnchor from "../components/LinkAnchor";
import SubjectView from "./SubjectLine";

const FileReaderAsync = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            resolve(reader.result as string);
        };
        reader.onerror = reject;

        reader.readAsDataURL(file);
    });
};

const Subject = () => {
    const navigate = useNavigate();

    const [messages, setMessage] = useState([] as any[]);
    const [subjects, setSubjects] = useState([] as any[]);

    const [currentSubject, setCurrentSubject] = useState({} as any);
    const [currentSubjectIndex, setCurrentSubjectIndex] = useState(0);

    const [sourcePhotos, setSourcePhotos] = useState([] as any[]);
    const [sharePhotos, setSharePhotos] = useState([] as any[]);

    const [showLogsModal, setShowLogsModal] = useState(false);

    const [newLogs, setNewLogs] = useState(false);

    const [devices, setDevices] = useState([] as string[]);
    const [currentDevice, setCurrentDevice] = useState("");

    SignalRContext.useSignalREffect(
        "ReceiveMessage",
        (message) => {
            setMessage([message, ...messages]);
            setNewLogs(true);
            let parts = message.split(":");
            if (parts[0] === "Subject" && (parts[1] == "create" || parts[1] == "update")) {
                loadSubject(parts[2]);
            }
        },
        []
    );

    SignalRContext.useSignalREffect(
        "DevicesUpdated",
        (message) => {
            loadDevices();
        },
        []
    );

    useEffect(() => {
        loadSubjects();
        loadDevices();
    }, []);

    const loadDevices = () => {
        axios.get("/api/Devices").then((response) => {
            setDevices(response.data || []);
        });
    };

    const loadSubjects = () => {
        axios.get("/api/Subject").then((response) => {
            let subjects = orderBy(response.data, "id");
            setSubjects(subjects);
            setCurrentSubject(subjects[currentSubjectIndex]);
        });
    };

    const loadSubject = (id) => {
        axios.get("/api/Subject/" + id).then((response) => {
            let newSubjects = orderBy(subjects.filter((subject) => subject.id !== id).concat([response.data]), "id");
            setSubjects(newSubjects);
            if (currentSubjectIndex == -1) {
                console.log("new subject");
                let index = newSubjects.findIndex((subject) => subject.id == id);
                setCurrentSubjectIndex(index);
                setCurrentSubject(newSubjects[index]);
            } else {
                console.log("subject " + currentSubjectIndex);
                setCurrentSubject(newSubjects[currentSubjectIndex]);
            }
        });
    };

    const getPrompt = (file) => {
        return exifr.parse(file, { chunked: false }).then((exif) => {
            try {
                let matches = /"prompt_input": "([^"]+)",/.exec(exif.prompt);
                let comfyui = (matches || [])[1];

                if (!comfyui) {
                    matches = /"text_g": "([^"]+)",/.exec(exif.prompt);
                    comfyui = (matches || [])[1];

                    if (!comfyui) {
                        let automatic = exif.parameters.split("\nNegative prompt:")[0];
                        return automatic;
                    }
                }

                return comfyui;
            } catch (e) {}

            return "";
        });
    };

    const nextSubject = () => {
        setCurrentSubjectIndex((currentSubjectIndex + 1) % subjects.length);
        setCurrentSubject(subjects[(currentSubjectIndex + 1) % subjects.length]);
    };

    const previousSubject = () => {
        setCurrentSubjectIndex((currentSubjectIndex - 1 + subjects.length) % subjects.length);
        setCurrentSubject(subjects[(currentSubjectIndex - 1 + subjects.length) % subjects.length]);
    };

    const generateName = (subject) => {
        Promise.all(subject.sharePhotos.map((sharePhoto) => getPrompt(sharePhoto))).then((prompts) => {
            axios.post("/api/subject/generateName", { subjectID: currentSubject.id, prompts });
        });
    };

    const removePhoto = async (subject, uri) => {
        await axios.delete(`/api/subject/${subject.id}/${encodeURIComponent(uri)}`);
    };

    const handleKeyDown = (event) => {
        if (event.key == "ArrowLeft") {
            previousSubject();
        } else if (event.key == "ArrowRight") {
            nextSubject();
        } else if (event.key == " " && event.ctrlKey) {
            alert("search");
        }
    };

    React.useEffect(() => {
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [handleKeyDown]);

    const showLogs = () => {
        setShowLogsModal(true);
        setNewLogs(false);
    };

    const mirrorCurrent = () => {
        axios.post("/api/devices/mirror", { subjectID: currentSubject.id, connectionID: currentDevice });
    };

    const [filter, setFilter] = useState("");

    const size = useContext(ResponsiveContext);

    const [sourceUploading, setSourceUploading] = useState(false);
    const [shareUploading, setShareUploading] = useState(false);

    const [subjectPage, setSubjectPage] = useState(0);

    const nextPage = () => {
        setSubjectPage(Math.min(subjectPage + 10, subjects.length));
    };

    const previousPage = () => {
        setSubjectPage(Math.max(subjectPage - 10, subjects.length));
    };

    return (
        <Box justify="center" align="center">
            <Box direction="row" gap="small" justify="end">
                <Button icon={<FontAwesomeIcon icon="arrow-left"></FontAwesomeIcon>} onClick={previousSubject}></Button>
                <Button icon={<FontAwesomeIcon icon="arrow-right"></FontAwesomeIcon>} onClick={nextSubject}></Button>
            </Box>
            <Box align="center">
                <Box pad="small">
                    <Button
                        label="New"
                        onClick={() => {
                            setCurrentSubjectIndex(-1);
                            axios.post("/api/subject", { name: Math.floor(Math.random() * 10000).toString() });
                        }}
                    ></Button>
                </Box>
                {currentSubject && (
                    <Box pad={"small"} border={{ size: "4px" }} round="10px">
                        <Box direction="row" justify="between">
                            <Box direction="row">
                                <Box>
                                    <Text>{currentSubject.name}</Text>
                                    <Text size="small" color={"background-front"}>
                                        {currentSubject.id}
                                    </Text>
                                </Box>
                                <Button tip={"Recreate Name"} icon={<FontAwesomeIcon icon="magic-wand-sparkles"></FontAwesomeIcon>} onClick={() => generateName(currentSubject)}></Button>
                            </Box>
                            <Box>
                                <Button
                                    tip={"Logs"}
                                    icon={
                                        <span className="fa-layers fa-fw fa-lg">
                                            <FontAwesomeIcon icon="list" />
                                            {newLogs && <FontAwesomeIcon color="red" icon="circle" transform="shrink-8 up-8 right-10" />}
                                        </span>
                                    }
                                    onClick={() => showLogs()}
                                ></Button>
                            </Box>
                        </Box>
                        <Grid columns={{ count: size == "small" ? 1 : 2, size: "auto" }}>
                            <Box>
                                <Text>Source Images</Text>
                                <Box direction="row" justify="between">
                                    <Box direction="row" gap="small" height={"200px"} pad={"xsmall"} wrap>
                                        {currentSubject.sourcePhotos?.map((sourcePhoto) => (
                                            <Box key={sourcePhoto} justify="between">
                                                <Image src={sourcePhoto} height={"150px"}></Image>
                                                <Button size="small" label="Remove" onClick={() => removePhoto(currentSubject, sourcePhoto)}></Button>
                                            </Box>
                                        ))}
                                        {sourceUploading && <Text>Uploading...</Text>}
                                    </Box>

                                    <FileInput
                                        value={sourcePhotos}
                                        accept="image/*"
                                        name="sourcePhoto"
                                        messages={{
                                            dropPrompt: "Add Image",
                                        }}
                                        className="accept-drop"
                                        onChange={(event) => {
                                            if (event) {
                                                setSourceUploading(true);
                                                const fileList = event.target.files;
                                                if (fileList && fileList.length > 0) {
                                                    if (sum(Array.from(fileList).map((f) => f.size)) > 10000000) {
                                                        alert("Total file size too large, <10MB");
                                                    } else {
                                                        Promise.all(Array.from(fileList).map((file) => FileReaderAsync(file).then((f) => ({ fileName: file.name, content: f })))).then(async (results: { fileName: string; content: string }[]) => {
                                                            for (let i = 0; i < results.length; i++) {
                                                                let f = results[i];
                                                                await axios.post("/api/subject/uploadSourceImage", { subjectID: currentSubject.id, fileName: f.fileName, content: f.content });
                                                            }
                                                            setSourceUploading(false);
                                                        });
                                                    }
                                                }
                                            }
                                        }}
                                    />
                                </Box>
                            </Box>
                            <Box align="end">
                                <LinkAnchor path={"/view/" + currentSubject.id} label={"Share"}></LinkAnchor>
                                <QRCodeView text={"https://" + window.location.host + "/view/" + currentSubject.id}></QRCodeView>
                            </Box>
                        </Grid>

                        <Text>Share Images</Text>
                        <Box direction="row" justify="between">
                            <Box direction="row" gap="small" height={{ min: "200px" }} pad={"xsmall"} wrap>
                                {currentSubject.sharePhotos?.map((sharePhoto) => (
                                    <Box key={sharePhoto} justify="between">
                                        <Image src={sharePhoto} height={"150px"}></Image>
                                        <Button size="small" label="Remove" onClick={() => removePhoto(currentSubject, sharePhoto)}></Button>
                                    </Box>
                                ))}
                                {shareUploading && <Text>Uploading...</Text>}
                            </Box>

                            <FileInput
                                value={sharePhotos}
                                accept="image/*"
                                name="genImages"
                                multiple
                                messages={{
                                    dropPromptMultiple: "Add Image",
                                }}
                                className="accept-drop"
                                onDrop={async (event) => {
                                    if (event.dataTransfer.items.length == 4) {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        let file = event.dataTransfer.items[3].getAsFile();
                                        if (file == null) {
                                            return;
                                        }
                                        setShareUploading(true);

                                        //@ts-ignore
                                        let f = await FileReaderAsync(file).then((f) => ({ fileName: file.name, content: f }));

                                        await axios.post("/api/subject/uploadShareImage", { subjectID: currentSubject.id, fileName: f.fileName, content: f.content });

                                        setShareUploading(false);
                                    }
                                }}
                                onChange={(event) => {
                                    if (event) {
                                        event.preventDefault();
                                        setShareUploading(true);
                                        const fileList = event.target.files;
                                        if (fileList && fileList.length > 0) {
                                            if (sum(Array.from(fileList).map((f) => f.size)) > 10000000) {
                                                alert("Total file size too large, <10MB");
                                            } else {
                                                Promise.all(Array.from(fileList).map((file) => FileReaderAsync(file).then((f) => ({ fileName: file.name, content: f })))).then(async (results: { fileName: string; content: string }[]) => {
                                                    for (let i = 0; i < results.length; i++) {
                                                        let f = results[i];
                                                        await axios.post("/api/subject/uploadShareImage", { subjectID: currentSubject.id, fileName: f.fileName, content: f.content });
                                                    }
                                                    setShareUploading(false);
                                                });
                                            }
                                        }
                                    }
                                }}
                            />
                        </Box>
                        <Box direction="row" gap="small" justify="between" pad={{ top: "small" }}>
                            <Box direction="row" gap="small">
                                <Select value={currentDevice} options={devices} onChange={({ value }) => setCurrentDevice(value)}></Select>
                                <Button icon={<FontAwesomeIcon icon="images"></FontAwesomeIcon>} onClick={mirrorCurrent}></Button>
                            </Box>
                            <Box>
                                <Button
                                    label="Delete"
                                    onClick={() => {
                                        if (confirm("Delete subject and photos?")) axios.delete("/api/subject/" + currentSubject.id);
                                    }}
                                ></Button>
                            </Box>
                        </Box>
                    </Box>
                )}

                <StyledModal isOpen={showLogsModal} setIsOpen={() => setShowLogsModal(false)}>
                    <Box width={"medium"}>
                        <Tabs>
                            <Tab title="Logs">
                                {messages.map((message, index) => (
                                    <Log
                                        subjects={subjects}
                                        key={index}
                                        log={message}
                                        onSelect={(id) => {
                                            let index = subjects.findIndex((subject) => subject.id == id);
                                            setCurrentSubjectIndex(index);
                                            setCurrentSubject(subjects[index]);
                                            setShowLogsModal(false);
                                        }}
                                    ></Log>
                                ))}
                            </Tab>
                            <Tab title="Subjects">
                                <TextInput value={filter} onChange={(e) => setFilter(e.target.value)}></TextInput>
                                {orderBy(subjects, "id", "desc")
                                    .filter((s, i) => new RegExp(filter).test(s.name))
                                    .filter((s, i) => (i >= subjectPage && i < subjectPage + 10))
                                    .map((subject, index) => (
                                        <SubjectView
                                            subject={subject}
                                            onSelect={(id) => {
                                                let index = subjects.findIndex((subject) => subject.id == id);
                                                setCurrentSubjectIndex(index);
                                                setCurrentSubject(subjects[index]);
                                                setShowLogsModal(false);
                                            }}
                                        ></SubjectView>
                                    ))}
                                <Box direction="row" justify="between">
                                    <Button label="Previous" onClick={previousPage}></Button>
                                    {subjectPage + 1} - {Math.min(subjectPage + 10, subjects.length)} / {subjects.length}
                                    <Button label="Next" onClick={nextPage}></Button>
                                </Box>
                            </Tab>
                        </Tabs>
                    </Box>
                </StyledModal>
            </Box>
        </Box>
    );
};

export default Subject;
